import React from 'react';

/**
 * Formats text to preserve line breaks, bold, and italic formatting
 * @param {string} text - The text to format
 * @returns {JSX.Element} - Formatted text component
 */
export const formatText = (text) => {
  if (!text) return null;

  // Split text by line breaks first
  const lines = text.split('\n');
  
  return (
    <>
      {lines.map((line, lineIndex) => {
        // Process each line for bold and italic formatting
        const formattedLine = formatInlineText(line);
        
        return (
          <React.Fragment key={lineIndex}>
            {formattedLine}
            {lineIndex < lines.length - 1 && <br />}
          </React.Fragment>
        );
      })}
    </>
  );
};

/**
 * Formats inline text for bold and italic formatting
 * @param {string} text - The text to format
 * @returns {JSX.Element} - Formatted inline text
 */
const formatInlineText = (text) => {
  if (!text) return null;

  // Regular expressions for bold and italic
  const boldRegex = /\*\*(.*?)\*\*/g;
  const italicRegex = /\*(.*?)\*/g;
  
  // Find all bold and italic matches
  const matches = [];
  
  // Find bold matches
  let match;
  while ((match = boldRegex.exec(text)) !== null) {
    matches.push({
      start: match.index,
      end: match.index + match[0].length,
      type: 'bold',
      content: match[1]
    });
  }
  
  // Reset regex
  boldRegex.lastIndex = 0;
  
  // Find italic matches
  while ((match = italicRegex.exec(text)) !== null) {
    matches.push({
      start: match.index,
      end: match.index + match[0].length,
      type: 'italic',
      content: match[1]
    });
  }
  
  // Sort matches by start position
  matches.sort((a, b) => a.start - b.start);
  
  // Remove overlapping matches (prioritize bold over italic)
  const filteredMatches = [];
  for (let i = 0; i < matches.length; i++) {
    const current = matches[i];
    const hasOverlap = filteredMatches.some(existing => 
      (current.start < existing.end && current.end > existing.start)
    );
    if (!hasOverlap) {
      filteredMatches.push(current);
    }
  }
  
  // Build the result
  let currentIndex = 0;
  const parts = [];
  
  filteredMatches.forEach((match, index) => {
    // Add text before the match
    if (match.start > currentIndex) {
      parts.push({
        type: 'text',
        content: text.slice(currentIndex, match.start)
      });
    }
    
    // Add the formatted match
    parts.push({
      type: match.type,
      content: match.content
    });
    
    currentIndex = match.end;
  });
  
  // Add remaining text
  if (currentIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.slice(currentIndex)
    });
  }
  
  // If no formatting found, return the original text
  if (parts.length === 0) {
    return text;
  }
  
  // Render the parts
  return (
    <>
      {parts.map((part, index) => {
        switch (part.type) {
          case 'bold':
            return <strong key={index}>{part.content}</strong>;
          case 'italic':
            return <em key={index}>{part.content}</em>;
          default:
            return part.content;
        }
      })}
    </>
  );
};

/**
 * Formats text for display in Typography components
 * @param {string} text - The text to format
 * @param {Object} sx - Additional styling
 * @returns {JSX.Element} - Formatted text with proper styling
 */
export const FormattedText = ({ text, sx = {} }) => {
  return (
    <span style={{ whiteSpace: 'pre-wrap', ...sx }}>
      {formatText(text)}
    </span>
  );
};
